import { Button, InputAmount, InputWrapper } from '@vrplatform/components';
import { showApiErrorNotification, useApiMutation } from '@vrplatform/data';
import { Box, Group, Text } from '@mantine/core';
import { Modal } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

type Props = {
  opened: boolean;
  closeModal: () => void;
  refetch: () => void;
};

type FormInputs = {
  taxesPayableCentTotal: number;
};

export const TaxesPayableModal = ({
  opened,
  closeModal,
  taxesPayableCentTotal,
  refetch,
}: Props & FormInputs) => {
  'use memo';

  const values = useMemo(
    () => ({
      taxesPayableCentTotal,
    }),
    [taxesPayableCentTotal]
  );

  const methods = useForm<FormInputs>({
    values,
  });

  const { mutateAsync } = useApiMutation(
    'put',
    '/teams/opening-balances/tax-payable'
  );

  const submit = async (data: FormInputs) => {
    await mutateAsync({
      body: {
        amount: data.taxesPayableCentTotal,
      },
    })
      .then(() => {
        closeModal();
        refetch();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to update taxes payable',
          defaultMessage:
            'Please try again later and contact support if the issue persists.',
        });
      });
  };

  const resetModal = useCallback(() => {
    closeModal();
    methods.reset(values);
  }, [closeModal, methods, values]);

  return (
    <Modal
      opened={opened}
      onClose={resetModal}
      size={500}
      title={'Add Taxes Payable'}
    >
      <Text c="neutral" mb="xs">
        Enter the total amount your team still owes for taxes from periods
        before your VRP start date.
      </Text>
      <Text c="neutral" mb="lg">
        This is a single team-level figure — if you're unsure, check your
        accounting records for the last closed period before switching to VRP.
      </Text>

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={resetModal}
      >
        <Box mb={'xl'}>
          <Controller
            control={methods.control}
            name="taxesPayableCentTotal"
            rules={{
              required: 'Taxes payable is required',
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label="Taxes Payable (total for the team)"
                error={fieldState.error?.message}
              >
                <InputAmount
                  value={
                    field.value === undefined
                      ? ''
                      : Number(field.value || '0') / 100
                  }
                  setValue={(value) => field.onChange(Number(value || 0) * 100)}
                  decimalScale={2}
                  error={!!fieldState.error?.message}
                  placeholder="0.00"
                  leftSection={<span>$</span>}
                />
              </InputWrapper>
            )}
          />
        </Box>
        <Group justify="right" my={5}>
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={methods.formState.isSubmitting}
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
